export const CONTENT_DELIVERABLE_OPTIONS_BY_PLATFORM = {
  Instagram: [
    'Instagram Live',
    'Instagram Photo Feed Post',
    'Instagram Reel',
    'Instagram Story',
  ],
  TikTok: [
    'TikTok Live',
    'TikTok Story',
    'TikTok Video',
  ],
  Whatnot: [
    'Whatnot Livestream',
  ],
};

export const LIVESTREAM_CONTENT_TYPES = [
  'Instagram Live',
  'TikTok Live',
  'Whatnot Livestream',
];

export const STORY_CONTENT_TYPES = [
  'Instagram Story',
  'TikTok Story',
];

export const DELIVERABLE_WORKFLOW = {
  LIVESTREAM: 'livestream',
  STORY: 'story',
  SHORT_FORM: 'short_form',
};

export function getContentDeliverableOptions(platformType) {
  return CONTENT_DELIVERABLE_OPTIONS_BY_PLATFORM[platformType] || [];
}

export function buildDefaultContentDeliverable(platformType, previous = {}) {
  const options = getContentDeliverableOptions(platformType);
  const contentType = options.includes(previous.contentType)
    ? previous.contentType
    : (options[0] || '');

  return {
    id: previous.id || 'primary',
    platform: platformType || '',
    contentType,
    guidelines: previous.guidelines || '',
  };
}

export function parseContentDeliverables(value) {
  if (Array.isArray(value)) return value;
  if (typeof value !== 'string') return [];
  try {
    const parsed = JSON.parse(value);
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
}

export function getCampaignContentDeliverables(campaign) {
  const deliverables = parseContentDeliverables(
    campaign?.content_deliverables ?? campaign?.contentDeliverables,
  )
    .map((deliverable, index) => ({
      id: deliverable.id || `deliverable-${index}`,
      platform: deliverable.platform || campaign?.platform_type || '',
      contentType: deliverable.contentType || deliverable.content_type || '',
      guidelines: deliverable.guidelines || '',
    }))
    .filter((deliverable) => deliverable.contentType);

  if (deliverables.length > 0) return deliverables;
  if (!campaign?.platform_type) return [];
  return [buildDefaultContentDeliverable(campaign.platform_type)];
}

export function getInfluencerContentDeliverables(influencer) {
  if (Array.isArray(influencer?.offerings) && influencer.offerings.length > 0) {
    return influencer.offerings
      .map((offering, index) => ({
        id: offering.id || `offering-${index}`,
        platform: offering.platform || '',
        contentType: offering.content_type || offering.contentType || '',
      }))
      .filter((deliverable) => deliverable.platform && deliverable.contentType);
  }

  return parseContentDeliverables(
    influencer?.content_deliverables ?? influencer?.contentDeliverables,
  )
    .map((deliverable, index) => ({
      id: deliverable.id || `deliverable-${index}`,
      platform: deliverable.platform || '',
      contentType: deliverable.contentType || deliverable.content_type || '',
    }))
    .filter((deliverable) => deliverable.platform && deliverable.contentType);
}

export function formatCampaignContentDeliverable(deliverable) {
  if (!deliverable?.contentType) return '';
  return deliverable.contentType;
}

export function formatCampaignContentDeliverables(campaign) {
  return getCampaignContentDeliverables(campaign)
    .map(formatCampaignContentDeliverable)
    .filter(Boolean);
}

export function getCampaignPlatformLabels(campaign) {
  const deliverables = getCampaignContentDeliverables(campaign);
  const platforms = [...new Set(
    deliverables.map((deliverable) => deliverable.platform).filter(Boolean),
  )];
  if (platforms.length > 0) return platforms;

  const platformType = campaign?.platform_type ?? campaign?.platformType;
  if (typeof platformType === 'string' && platformType.trim()) {
    return [platformType.trim()];
  }
  return [];
}

export function formatCampaignPlatformLabel(campaign) {
  return getCampaignPlatformLabels(campaign).join(', ');
}

export function campaignMatchesPlatformFilter(campaign, platformFilter) {
  if (!platformFilter || platformFilter === 'all') return true;
  const needle = String(platformFilter).trim().toLowerCase();
  if (!needle) return true;
  return getCampaignPlatformLabels(campaign).some(
    (platform) => platform.toLowerCase() === needle,
  );
}

export function formatInfluencerContentDeliverables(influencer) {
  return getInfluencerContentDeliverables(influencer)
    .map((deliverable) => deliverable.contentType)
    .filter(Boolean);
}

export function isLivestreamContentType(contentType) {
  return LIVESTREAM_CONTENT_TYPES.includes(contentType);
}

export function isStoryContentType(contentType) {
  return STORY_CONTENT_TYPES.includes(contentType);
}

export function getDeliverableWorkflow(contentType) {
  if (isLivestreamContentType(contentType)) return DELIVERABLE_WORKFLOW.LIVESTREAM;
  if (isStoryContentType(contentType)) return DELIVERABLE_WORKFLOW.STORY;
  return DELIVERABLE_WORKFLOW.SHORT_FORM;
}

export function getPrimaryContentDeliverable(campaign) {
  return getCampaignContentDeliverables(campaign)[0] || null;
}

export function getCampaignDeliverableWorkflow(campaign) {
  return getDeliverableWorkflow(getPrimaryContentDeliverable(campaign)?.contentType);
}

export function campaignHasLivestreamDeliverables(campaign) {
  return getCampaignContentDeliverables(campaign)
    .some((deliverable) => isLivestreamContentType(deliverable.contentType));
}

export function influencerHasLivestreamContent(influencer) {
  return getInfluencerContentDeliverables(influencer)
    .some((deliverable) => isLivestreamContentType(deliverable.contentType));
}

export function campaignIsShortFormOnly(campaign) {
  const deliverables = getCampaignContentDeliverables(campaign);
  return deliverables.length > 0
    && !deliverables.some((deliverable) => isLivestreamContentType(deliverable.contentType));
}

export function getPrimaryDeliverableType(campaign) {
  return campaignHasLivestreamDeliverables(campaign) ? 'livestream' : 'post';
}
